import type { Script } from '@/types/domain';

export interface RoleConflict {
  sceneId: string;
  sceneName: string;
  /** Character ids the actor chose that all speak in this scene. */
  characterIds: string[];
}

/**
 * Scenes where the chosen roles would have to be on stage at the same time.
 *
 * An actor can double parts across a show, but not within one scene, so this
 * is what makes a role selection impossible to perform.
 */
export function roleConflicts(script: Script, roles: string[]): RoleConflict[] {
  if (roles.length < 2) return [];
  const mine = new Set(roles);
  const conflicts: RoleConflict[] = [];

  for (const scene of script.scenes) {
    const present = new Set<string>();
    for (const line of scene.lines) {
      if (line.kind === 'dialogue' && mine.has(line.character)) present.add(line.character);
    }
    if (present.size > 1) {
      conflicts.push({
        sceneId: scene.sceneId,
        sceneName: scene.name,
        characterIds: [...present],
      });
    }
  }

  return conflicts;
}

/** Character ids that clash with an already-chosen role somewhere in the show. */
export function rolesClashingWith(script: Script, roles: string[], candidate: string): boolean {
  return roleConflicts(script, [...roles, candidate]).length > 0;
}
